/**
 * API publications multi-plateforme (S6 PR2-D quater + PR2-C).
 *
 * Routes paramétrées :
 *   - GET    /api/works/{id}/publications/{platform}
 *   - POST   /api/works/{id}/publications/{platform}
 *   - PATCH  /api/works/{id}/publications/{platform}/{resource_id}
 *   - DELETE /api/works/{id}/publications/{platform}/{resource_id}?reason=...
 *   - POST   /api/works/{id}/publications/{platform}/{resource_id}/renew
 *
 * En S6, seul `platform='etsy'` est branché côté BE. Pinterest/Instagram/
 * TikTok lèvent 422 "pas encore implémentée" (filtrage côté service).
 */

import { apiClient } from './client'
import type { components } from '@/types/api-generated'
import type {
  Platform,
  PlatformPublishEnqueueResponse,
  PlatformPublishRequest,
  PlatformResourceResponse,
} from '@/types/publications'

type PlatformPublicationUpdateRequest = components['schemas']['PlatformPublicationUpdateRequest']
type PlatformPublicationLifecycleResponse =
  components['schemas']['PlatformPublicationLifecycleResponse']

/** Shape de la réponse `POST /publications/{platform}/{rid}/delete`. Défini
 * inline plutôt que pulled depuis `components['schemas']` parce que le
 * codegen openapi/api-generated n'a pas encore été régénéré (travail
 * Pinterest S7 parallèle en cours qui pollue le diff). À remplacer par
 * `components['schemas']['DeleteWithReconciliationResponse']` au prochain
 * regen propre. Le BE Pydantic est la source de vérité (cf.
 * `backend/app/schemas/publications.py:DeleteWithReconciliationResponse`).
 */
export interface DeleteWithReconciliationResponse {
  queue_item_id: number | null
  platform: Platform
  platform_resource_id: number
  status: 'scheduled' | 'reconciled_local_only'
  etsy_was_present: boolean
  purged_queue_items: number[]
}

export type EndPublicationReason = 'user_unpublish' | 'sold_direct'

export const workPublicationsApi = {
  list: async (workId: number, platform: Platform): Promise<PlatformResourceResponse[]> => {
    const { data } = await apiClient.get<PlatformResourceResponse[]>(
      `/works/${workId}/publications/${platform}`,
    )
    return data
  },

  enqueuePublish: async (
    workId: number,
    platform: Platform,
    payload: PlatformPublishRequest,
  ): Promise<PlatformPublishEnqueueResponse> => {
    const { data } = await apiClient.post<PlatformPublishEnqueueResponse>(
      `/works/${workId}/publications/${platform}`,
      payload,
    )
    return data
  },

  updatePublication: async (
    workId: number,
    platform: Platform,
    resourceId: number,
    payload: PlatformPublicationUpdateRequest,
  ): Promise<PlatformPublicationLifecycleResponse> => {
    const { data } = await apiClient.patch<PlatformPublicationLifecycleResponse>(
      `/works/${workId}/publications/${platform}/${resourceId}`,
      payload,
    )
    return data
  },

  endPublication: async (
    workId: number,
    platform: Platform,
    resourceId: number,
    reason: EndPublicationReason,
  ): Promise<PlatformPublicationLifecycleResponse> => {
    const { data } = await apiClient.delete<PlatformPublicationLifecycleResponse>(
      `/works/${workId}/publications/${platform}/${resourceId}`,
      { params: { reason } },
    )
    return data
  },

  renewPublication: async (
    workId: number,
    platform: Platform,
    resourceId: number,
  ): Promise<PlatformPublicationLifecycleResponse> => {
    const { data } = await apiClient.post<PlatformPublicationLifecycleResponse>(
      `/works/${workId}/publications/${platform}/${resourceId}/renew`,
    )
    return data
  },

  /**
   * Suppression intelligente — le BE probe la plateforme avant d'agir.
   *
   * Deux chemins possibles via `status` :
   *   - `"scheduled"` : listing présent côté Etsy, job `delete_publication`
   *     posé dans la file. Le FE poll `GET /publications/{platform}` pour
   *     observer la bascule.
   *   - `"reconciled_local_only"` : listing absent côté Etsy (probe 404 —
   *     supprimé manuellement sur etsy.com hors-app). La bascule
   *     `state='deleted'` + la purge des queue_items orphelins se font
   *     dans la transaction BE, instantané.
   *
   * Garde-fou : refusé (409) si listing présent côté Etsy ET state DB
   * ≠ `ended` (Stéphanie doit cliquer « Retirer » avant « Supprimer »).
   */
  deleteWithReconciliation: async (
    workId: number,
    platform: Platform,
    resourceId: number,
  ): Promise<DeleteWithReconciliationResponse> => {
    const { data } = await apiClient.post<DeleteWithReconciliationResponse>(
      `/works/${workId}/publications/${platform}/${resourceId}/delete`,
    )
    return data
  },

  /**
   * S7-PR2 — Marque en lot des Pins Pinterest comme supprimés côté
   * atelier (local-only, ne touche pas l'API Pinterest). Utilisé par
   * le modal "Détecter doublons" sur `<WorkPublicationsTab>`.
   */
  markPinterestDuplicatesDeleted: async (
    workId: number,
    resourceIds: number[],
  ): Promise<PinterestMarkDuplicatesResponse> => {
    const { data } = await apiClient.post<PinterestMarkDuplicatesResponse>(
      `/works/${workId}/publications/pinterest/mark-duplicates-deleted`,
      { resource_ids: resourceIds },
    )
    return data
  },
}

export type PinterestMarkDuplicatesResponse =
  components['schemas']['PinterestMarkDuplicatesResponse']
